import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";

import { cx } from "../app/format";

export const TECHNICAL_DETAILS_LABELS = {
  summary: "Technical details",
  summaryHelper: "Sources, checks and raw data",
  sources: "Sources",
  checks: "Checks",
  rawData: "Raw data",
  downloads: "Downloads",
  showRawData: "Show raw data",
  hideRawData: "Hide raw data",
  downloadRawData: "Download raw data",
  downloadTechnicalCsv: "Download technical CSV",
  emptyEvidence: "No technical evidence is available for this item."
} as const;

export type TechnicalDetailsTone = "neutral" | "info" | "success" | "warning" | "risk";

export interface TechnicalDetailsEntry {
  label: string;
  detail?: ReactNode;
  meta?: ReactNode;
  tone?: TechnicalDetailsTone;
}

export type TechnicalDetailsDownloadKind = "raw-data" | "technical-csv";

export interface TechnicalDetailsDownload {
  kind: TechnicalDetailsDownloadKind;
  href?: string;
  download?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface TechnicalDetailsProps {
  /**
   * Chain A shared API for all C5 local technical depth.
   *
   * Component chrome labels are fixed through `TECHNICAL_DETAILS_LABELS`:
   * summary `Technical details`, optional helper `Sources, checks and raw data`,
   * section headings `Sources`/`Checks`/`Raw data`/`Downloads`, nested raw
   * toggle `Show raw data`/`Hide raw data`, downloads `Download raw data` and
   * `Download technical CSV`, and empty evidence copy
   * `No technical evidence is available for this item.`. Evidence entry labels
   * are caller content, but no caller can override the component label
   * vocabulary.
   *
   * `sources` and `checks` are human-readable first-level evidence entries.
   * Machine ids, refs, provider/runtime payloads, tool calls, verifier output,
   * and exact JSON belong in `rawData`, which is hidden behind the nested raw
   * toggle. `downloads` accepts only the two contract download kinds so raw and
   * technical CSV actions cannot invent local labels. State is transient and
   * component-local; it is never persisted to storage, URL, project, server, or
   * global context. The C5 sweep registry consumes `TechnicalDetailsEntry` arrays
   * plus `technicalProofPatterns` per surface.
   */
  sources?: readonly TechnicalDetailsEntry[];
  checks?: readonly TechnicalDetailsEntry[];
  rawData?: unknown;
  downloads?: readonly TechnicalDetailsDownload[];
  showSummaryHelper?: boolean;
  className?: string;
  idPrefix?: string;
  "data-testid"?: string;
}

const toneClass: Record<TechnicalDetailsTone, string> = {
  neutral: "border-[var(--line2)] bg-[var(--raised2)] text-[var(--ink2)]",
  info: "border-[var(--info-border)] bg-[var(--info-bg)] text-[var(--info)]",
  success:
    "border-[var(--success-border)] bg-[var(--success-bg)] text-[var(--success)]",
  warning: "border-[var(--warn-border)] bg-[var(--warn-bg)] text-[var(--warn)]",
  risk: "border-[var(--risk-border)] bg-[var(--risk-bg)] text-[var(--risk)]"
};

export function TechnicalDetails({
  sources = [],
  checks = [],
  rawData,
  downloads = [],
  showSummaryHelper = true,
  className,
  idPrefix,
  "data-testid": testId
}: TechnicalDetailsProps) {
  const reactId = useId();
  const safeId = (idPrefix ?? `technical-details-${reactId}`).replace(/:/g, "");
  const buttonId = `${safeId}-toggle`;
  const panelId = `${safeId}-panel`;
  const rawPanelId = `${safeId}-raw`;
  const rawHeadingId = `${safeId}-raw-heading`;
  const [open, setOpen] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const hasRawData = rawData !== undefined;
  const hasEvidence =
    sources.length > 0 || checks.length > 0 || downloads.length > 0 || hasRawData;
  const rawDataText = useMemo(
    () => (hasRawData ? formatRawData(rawData) : ""),
    [hasRawData, rawData]
  );

  useEffect(() => {
    if (!open) {
      setShowRawData(false);
    }
  }, [open]);

  const closeFromPanel = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closeFromPanel();
    }
  };

  return (
    <div
      className={cx("min-w-0 text-left", className)}
      data-technical-details
      data-testid={testId}
    >
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex cursor-pointer items-center gap-2 rounded-[9px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 py-1.5 text-[10.5px] font-extrabold text-[var(--muted)] transition-colors hover:bg-[var(--chip)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ink)] motion-reduce:transition-none"
        data-technical-details-toggle
      >
        <span
          aria-hidden
          className={cx(
            "grid h-4 w-4 place-items-center rounded-[5px] border border-[var(--line3)] text-[11px] leading-none transition-transform motion-reduce:transition-none",
            open && "rotate-90"
          )}
        >
          ›
        </span>
        <span>{TECHNICAL_DETAILS_LABELS.summary}</span>
        {showSummaryHelper ? (
          <span aria-hidden className="hidden text-[var(--faint)] sm:inline">
            {TECHNICAL_DETAILS_LABELS.summaryHelper}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          onKeyDown={handlePanelKeyDown}
          className="mt-2 rounded-[10px] border border-[var(--line)] bg-[var(--surface)] p-3 shadow-[0_14px_34px_-30px_rgba(35,33,30,.82)]"
          data-technical-details-panel
        >
          {hasEvidence ? (
            <div className="grid gap-3">
              <TechnicalDetailsSection
                heading={TECHNICAL_DETAILS_LABELS.sources}
                entries={sources}
              />
              <TechnicalDetailsSection
                heading={TECHNICAL_DETAILS_LABELS.checks}
                entries={checks}
              />
              {hasRawData ? (
                <section>
                  <h3
                    id={rawHeadingId}
                    className="text-[9.5px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]"
                  >
                    {TECHNICAL_DETAILS_LABELS.rawData}
                  </h3>
                  <button
                    type="button"
                    aria-expanded={showRawData}
                    aria-controls={showRawData ? rawPanelId : undefined}
                    onClick={() => setShowRawData((current) => !current)}
                    className="mt-1.5 rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 py-1.5 text-[10.5px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ink)]"
                    data-technical-raw-toggle
                  >
                    {showRawData
                      ? TECHNICAL_DETAILS_LABELS.hideRawData
                      : TECHNICAL_DETAILS_LABELS.showRawData}
                  </button>
                  {showRawData ? (
                    <pre
                      id={rawPanelId}
                      aria-labelledby={rawHeadingId}
                      className="mt-2 max-h-[240px] overflow-auto rounded-[9px] bg-[var(--ink)] p-3 font-mono text-[10.5px] leading-[1.45] text-[var(--onSolid)]"
                      data-technical-raw-data
                    >
                      {rawDataText}
                    </pre>
                  ) : null}
                </section>
              ) : null}
              {downloads.length > 0 ? (
                <section>
                  <h3 className="text-[9.5px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
                    {TECHNICAL_DETAILS_LABELS.downloads}
                  </h3>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {downloads.map((download, index) => (
                      <TechnicalDownloadAction
                        key={`${download.kind}-${index}`}
                        download={download}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <p className="rounded-[9px] border border-dashed border-[var(--line3)] bg-[var(--raised2)] px-3 py-2 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]">
              {TECHNICAL_DETAILS_LABELS.emptyEvidence}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function TechnicalDetailsSection({
  heading,
  entries
}: {
  heading: string;
  entries: readonly TechnicalDetailsEntry[];
}) {
  return (
    <section>
      <h3 className="text-[9.5px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
        {heading}
      </h3>
      {entries.length > 0 ? (
        <div className="mt-1.5 grid gap-1.5">
          {entries.map((entry, index) => (
            <div
              key={`${entry.label}-${index}`}
              className={cx(
                "rounded-[9px] border px-2.5 py-2",
                toneClass[entry.tone ?? "neutral"]
              )}
            >
              <div className="text-[11.5px] font-extrabold leading-[1.35]">
                {entry.label}
              </div>
              {entry.detail ? (
                <div className="mt-0.5 text-[11px] font-semibold leading-[1.4]">
                  {entry.detail}
                </div>
              ) : null}
              {entry.meta ? (
                <div className="mt-1 text-[10px] font-semibold opacity-75">
                  {entry.meta}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-1.5 rounded-[9px] border border-dashed border-[var(--line3)] bg-[var(--raised2)] px-2.5 py-2 text-[11.5px] font-semibold leading-[1.4] text-[var(--muted)]">
          {TECHNICAL_DETAILS_LABELS.emptyEvidence}
        </p>
      )}
    </section>
  );
}

function TechnicalDownloadAction({ download }: { download: TechnicalDetailsDownload }) {
  const label =
    download.kind === "technical-csv"
      ? TECHNICAL_DETAILS_LABELS.downloadTechnicalCsv
      : TECHNICAL_DETAILS_LABELS.downloadRawData;
  const className =
    "rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 py-1.5 text-[10.5px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ink)]";

  if (download.href) {
    return (
      <a
        href={download.href}
        download={download.download}
        onClick={download.onClick}
        aria-disabled={download.disabled ? true : undefined}
        className={cx(className, download.disabled && "pointer-events-none opacity-55")}
      >
        {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={download.onClick}
      disabled={download.disabled}
      className={cx(className, "disabled:cursor-not-allowed disabled:opacity-55")}
    >
      {label}
    </button>
  );
}

function formatRawData(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
